import { useEffect, useMemo, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { ProgressBar } from "@/components/ProgressBar";
import { Button } from "@/components/ui/button";
import {
  BookOpen, Clock, TrendingUp, Loader2, Radio,
  GraduationCap, ArrowRight, Calendar, CheckCircle2,
  Circle
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/auth/AuthContext";
import {
  getCourseLiveSessions,
  getMyEnrollments,
  getMyProgress,
  formatCourseLevelsOr,
  type Enrollment,
  type UserProgress,
  type LiveSessionDetails,
} from "@/api/lms/courses";
import { formatRelativeTime, formatUpcomingSession } from "@/lib/formatRelative";

type LiveItem = LiveSessionDetails & { courseTitle: string };

function progressPct(p: UserProgress | undefined): number {
  if (!p) return 0;
  const n = parseFloat(String(p.progressPercentage ?? "0"));
  if (Number.isNaN(n)) return 0;
  return Math.min(100, Math.round(n));
}

function firstNameFromUser(email: string, firstName?: string) {
  if (firstName?.trim()) return firstName.trim();
  const prefix = (email.split("@")[0] ?? "").trim();
  const token = prefix.split(/[._-]+/)[0] ?? prefix;
  const cleaned = token.replace(/[0-9]+/g, "").trim();
  const name = cleaned || token || "there";
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export default function StudentDashboard() {
  const { user } = useAuth();
  const myCoursesPath = user?.backendRole === "TEACHER" ? "/teacher/courses" : "/courses";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [progress, setProgress] = useState<UserProgress[]>([]);
  const [upcomingLive, setUpcomingLive] = useState<LiveItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [en, pr] = await Promise.all([getMyEnrollments(), getMyProgress()]);
        if (cancelled) return;
        setEnrollments(en);
        setProgress(pr);

        const now = Date.now();
        const sessionsNested = await Promise.all(
          en.map(async (e) => {
            try {
              if (!e.course) return [];
              const list = await getCourseLiveSessions(e.course.id);
              return list.map((s) => ({ ...s, courseTitle: e.course.title }));
            } catch {
              return [];
            }
          }),
        );
        if (cancelled) return;
        const flat = sessionsNested.flat() as LiveItem[];
        const future = flat
          .filter((s) => new Date(s.startTime).getTime() >= now - 60000)
          .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
          .slice(0, 5);
        setUpcomingLive(future);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load dashboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const progressByCourse = useMemo(() => {
    const m = new Map<string, UserProgress>();
    progress.forEach((p) => m.set(p.courseId, p));
    return m;
  }, [progress]);

  const resume = useMemo(() => {
    const withDates = progress
      .filter((p) => p.lastAccessedAt)
      .sort(
        (a, b) =>
          new Date(b.lastAccessedAt!).getTime() - new Date(a.lastAccessedAt!).getTime(),
      );
    const top = withDates[0];
    if (top) {
      return {
        courseId: top.courseId,
        title: top.course?.title ?? "Your course",
        subtitle: "Pick up where you left off",
        pct: progressPct(top),
      };
    }
    const first = enrollments.find((e) => e.course);
    if (first && first.course) {
      return {
        courseId: first.course.id,
        title: first.course.title,
        subtitle: "Start your learning journey",
        pct: progressPct(progressByCourse.get(first.course.id)),
      };
    }
    return null;
  }, [progress, enrollments, progressByCourse]);

  const recentActivity = useMemo(() => {
    return progress
      .filter((p) => p.lastAccessedAt)
      .sort(
        (a, b) =>
          new Date(b.lastAccessedAt!).getTime() - new Date(a.lastAccessedAt!).getTime(),
      )
      .slice(0, 5)
      .map((p) => ({
        action: p.course?.title ?? "A course",
        time: formatRelativeTime(p.lastAccessedAt),
        pct: progressPct(p),
      }));
  }, [progress]);

  const inProgressCount = useMemo(() => {
    return progress.filter((p) => progressPct(p) > 0 && progressPct(p) < 100).length;
  }, [progress]);

  const completedCount = useMemo(() => {
    return progress.filter((p) => progressPct(p) >= 100).length;
  }, [progress]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="dash-page">
          <div className="dash-empty-state mt-20">
            <Loader2 className="animate-spin mb-2" size={24} />
            <span className="text-sm font-medium">Loading your dashboard…</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const displayName = firstNameFromUser(user?.email ?? "", undefined);

  return (
    <DashboardLayout>
      <div className="dash-page">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Page Header */}
          <div className="dash-page-header">
            <div>
              <h1 className="dash-page-title">
                Welcome back, {displayName} 👋
              </h1>
              <p className="dash-page-subtitle">
                {error
                  ? error
                  : enrollments.length === 0
                    ? "Browse the catalog and enroll in a course to get started."
                    : `You're enrolled in ${enrollments.length} course${enrollments.length === 1 ? "" : "s"}.`}
              </p>
            </div>
            <Button size="sm" asChild className="shrink-0">
              <Link to={myCoursesPath}>
                <BookOpen size={14} className="mr-1.5" /> My Courses
              </Link>
            </Button>
          </div>

          {/* Summary Stats Row */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <KCard className="dash-stat-card">
              <div className={`dash-stat-icon bg-primary/10 text-primary`}>
                <BookOpen size={18} />
              </div>
              <div>
                <p className="dash-stat-value">{enrollments.length}</p>
                <p className="dash-stat-label mt-1">Enrolled</p>
              </div>
            </KCard>
            <KCard className="dash-stat-card">
              <div className={`dash-stat-icon bg-amber-50 text-amber-600`}>
                <TrendingUp size={18} />
              </div>
              <div>
                <p className="dash-stat-value">{inProgressCount}</p>
                <p className="dash-stat-label mt-1">In Progress</p>
              </div>
            </KCard>
            <KCard className="dash-stat-card">
              <div className={`dash-stat-icon bg-emerald-50 text-emerald-600`}>
                <GraduationCap size={18} />
              </div>
              <div>
                <p className="dash-stat-value">{completedCount}</p>
                <p className="dash-stat-label mt-1">Completed</p>
              </div>
            </KCard>
          </div>

          {/* Main 2-column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Left: Main Content */}
            <div className="lg:col-span-2 space-y-6">

              {/* Hero: Continue Learning or Empty */}
              {resume ? (
                <KCard className="overflow-hidden relative bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground">
                  <div className="p-6 relative z-10">
                    <p className="text-primary-foreground/70 text-xs font-semibold uppercase tracking-widest mb-2">
                      Continue Learning
                    </p>
                    <h2 className="text-lg font-bold mb-1 leading-snug">{resume.title}</h2>
                    <p className="text-primary-foreground/70 text-sm mb-5">{resume.subtitle}</p>
                    <div className="mb-5">
                      <div className="flex justify-between text-xs mb-1.5 opacity-80">
                        <span>Progress</span>
                        <span className="font-bold">{resume.pct}%</span>
                      </div>
                      <div className="w-full bg-white/20 rounded-full h-1.5 overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${resume.pct}%` }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                          className="h-full bg-white rounded-full"
                        />
                      </div>
                    </div>
                    <Link to={`/course?courseId=${encodeURIComponent(resume.courseId)}`}>
                      <Button
                        variant="hero-secondary"
                        size="sm"
                        className="flex items-center gap-1.5"
                      >
                        Open Course <ArrowRight size={14} />
                      </Button>
                    </Link>
                  </div>
                  <BookOpen
                    size={140}
                    className="absolute right-[-16px] bottom-[-16px] opacity-[0.07]"
                  />
                </KCard>
              ) : (
                <div className="dash-empty-state">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <BookOpen size={28} className="text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground text-lg mb-2">No enrollments yet</h3>
                  <p className="text-sm text-muted-foreground mb-6">
                    Explore the course catalog and start learning.
                  </p>
                  <Link to="/">
                    <Button size="sm">Browse Courses</Button>
                  </Link>
                </div>
              )}

              {/* Enrolled Courses */}
              <div>
                <div className="dash-section-header">
                  <h2 className="dash-section-title">Enrolled Courses</h2>
                  <Link to={myCoursesPath} className="dash-section-link flex items-center gap-1">
                    All courses <ArrowRight size={12} />
                  </Link>
                </div>
                {enrollments.length === 0 ? (
                  <div className="dash-empty-state py-8">
                    <p className="text-sm font-medium text-muted-foreground">Enroll in a course to see it here.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {enrollments.filter((e) => e.course).slice(0, 6).map((e) => {
                      const pct = progressPct(progressByCourse.get(e.course.id));
                      const isComplete = pct >= 100;
                      return (
                        <Link
                          to={`/course?courseId=${encodeURIComponent(e.course.id)}`}
                          key={e.id}
                        >
                          <KCard hoverable className="p-4 h-full flex flex-col gap-3">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className="text-sm font-semibold text-foreground line-clamp-2 leading-snug flex-1">
                                {e.course.title}
                              </h4>
                              {isComplete && (
                                <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                              )}
                            </div>
                            <div className="mt-auto space-y-1.5">
                              <div className="flex justify-between text-xs">
                                <span className="text-muted-foreground">
                                  {formatCourseLevelsOr(e.course, "General")}
                                </span>
                                <span
                                  className={`font-semibold ${
                                    isComplete ? "text-emerald-600" : "text-foreground"
                                  }`}
                                >
                                  {pct}%
                                </span>
                              </div>
                              <ProgressBar
                                progress={pct}
                                variant={isComplete ? "success" : "primary"}
                              />
                            </div>
                          </KCard>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Recent Activity */}
              <div>
                <div className="dash-section-header">
                  <h2 className="dash-section-title">Recent Activity</h2>
                </div>
                <KCard className="overflow-hidden">
                  {recentActivity.length === 0 ? (
                    <div className="dash-empty-state py-10 border-none shadow-none bg-transparent">
                      <Circle size={28} className="text-muted-foreground/30 mb-2" />
                      <p className="text-sm font-medium">
                        Open a lesson to see your activity here.
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {recentActivity.map((item, i) => (
                        <div key={i} className="px-5 py-3.5 flex items-center justify-between gap-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">
                                {item.action}
                              </p>
                              <p className="text-[11px] text-muted-foreground">{item.pct}% complete</p>
                            </div>
                          </div>
                          <p className="text-[11px] text-muted-foreground whitespace-nowrap shrink-0">
                            {item.time}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </KCard>
              </div>
            </div>

            {/* Right: Sidebar Widgets */}
            <div className="space-y-5">

              {/* Upcoming Live Sessions */}
              <KCard className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Radio size={14} className="text-primary" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground">Live Sessions</h3>
                </div>
                {upcomingLive.length === 0 ? (
                  <div className="dash-empty-state py-6 border-none shadow-none bg-transparent">
                    <Calendar size={28} className="text-muted-foreground/30 mb-2" />
                    <p className="text-sm font-medium">No upcoming sessions.</p>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {upcomingLive.map((item) => (
                      <Link
                        key={item.id}
                        to={`/course?courseId=${encodeURIComponent(item.courseId ?? "")}`}
                        className="flex items-start gap-2.5 p-2.5 rounded-xl hover:bg-secondary/60 transition-colors"
                      >
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                          <Clock size={14} className="text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-foreground truncate">
                            {item.title}
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                            {item.courseTitle}
                          </p>
                          <p className="text-[11px] text-primary font-medium mt-0.5">
                            {formatUpcomingSession(item.startTime)}
                          </p>
                          {item.meetingLink && (
                            <a
                              href={item.meetingLink}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[11px] text-primary font-semibold mt-1 inline-flex items-center gap-0.5 hover:underline"
                              onClick={(ev) => ev.stopPropagation()}
                            >
                              Join <ArrowRight size={10} />
                            </a>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </KCard>

              {/* Learning Overview */}
              <KCard className="p-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <TrendingUp size={14} className="text-emerald-600" />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground">Overview</h3>
                </div>
                <div className="space-y-3">
                  {[
                    { label: "Enrolled courses", value: enrollments.length, icon: BookOpen },
                    { label: "In progress", value: inProgressCount, icon: TrendingUp },
                    { label: "Completed", value: completedCount, icon: CheckCircle2 },
                    { label: "Live sessions", value: upcomingLive.length, icon: Radio },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <row.icon size={13} className="text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">{row.label}</span>
                      </div>
                      <span className="text-sm font-bold text-foreground">{row.value}</span>
                    </div>
                  ))}
                </div>
              </KCard>

              {/* Quick Access */}
              <KCard className="p-5">
                <h3 className="text-sm font-semibold text-foreground mb-3">Quick Access</h3>
                <div className="space-y-1.5">
                  {[
                    { to: "/mock-tests", label: "Mock Tests", icon: GraduationCap },
                    { to: "/certificates", label: "My Certificates", icon: GraduationCap },
                    { to: "/community", label: "Community", icon: BookOpen },
                  ].map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-muted/50 transition-colors group"
                    >
                      <span className="text-xs font-medium text-foreground">{item.label}</span>
                      <ArrowRight size={12} className="text-muted-foreground group-hover:text-primary transition-colors" />
                    </Link>
                  ))}
                </div>
              </KCard>

            </div>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
